/* ─────────────────────────────────────────────
   BKlay Studio — the select menu.

   A closed <select> can be styled to match the rest
   of the form. The list it opens cannot: that list is
   drawn by the operating system, and `color-scheme`
   is the only part of it the page gets a say in. So a
   dark page opened a menu in macOS's own type at
   macOS's own spacing with macOS's own rounded corner,
   and the one control on the form that did not belong
   to the site was the one you had to use.

   The customisable-select spec hands that list over as
   a real element, but only Chrome implements it — which
   left three browsers looking three different ways.
   This builds the list instead, so all three look the
   same.

   Everything here is an upgrade on markup that already
   works. The <select> stays in the form and is still
   what submits; it is only taken off the screen. With
   this file gone, or failed, every menu on the site is
   an ordinary menu.
   ───────────────────────────────────────────── */
(function () {
  "use strict";

  /* Long enough to type a word into, short enough that the next word starts
     a new search. The figure the platforms themselves use. */
  var TYPEAHEAD_RESET = 500;

  /* Only ever one menu is open, and the document-level handlers below need to
     know which — an outside click, Escape and a resize all close it. */
  var openMenu = null;

  /* For the handful of selects that reach here without an id of their own. */
  var uid = 0;

  function label(option) {
    return (option.textContent || "").replace(/\s+/g, " ").trim();
  }

  function enhance(select) {
    if (select.getAttribute("data-select-menu")) return;

    /* A multiple or sized select is already an open list — there is no
       dropdown to replace, and base.css styles it as the list it is. */
    if (select.multiple) return;
    if (parseInt(select.getAttribute("size"), 10) > 1) return;

    var field = select.closest(".field");
    if (!field) return;

    select.setAttribute("data-select-menu", "on");

    var base = select.id || ("select-menu-" + (++uid));
    var active = -1;

    /* Grabbed before anything is rewired: once the label's `for` moves to the
       button below, `select.labels` is empty. */
    var name = select.labels && select.labels.length ? select.labels[0] : null;

    var wrap = document.createElement("div");
    wrap.className = "select-menu";
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);

    /* `type="button"` because this lives inside a form and the default is
       submit — without it, opening the menu posts the form. */
    var button = document.createElement("button");
    button.type = "button";
    button.className = "select-menu-button";
    button.id = base + "-button";
    button.setAttribute("aria-haspopup", "listbox");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-controls", base + "-list");

    var list = document.createElement("div");
    list.className = "select-menu-list";
    list.id = base + "-list";
    list.setAttribute("role", "listbox");
    list.hidden = true;

    var items = [];
    var i;
    for (i = 0; i < select.options.length; i++) {
      var item = document.createElement("div");
      item.className = "select-menu-option";
      item.id = base + "-option-" + i;
      item.setAttribute("role", "option");
      item.setAttribute("aria-selected", "false");
      if (select.options[i].disabled) item.setAttribute("aria-disabled", "true");
      item.textContent = label(select.options[i]);
      list.appendChild(item);
      items.push(item);
    }

    /* The <select> is what submits, so it stays; it is only hidden, and only
       from here, so that a visitor without this script still meets it. The
       pair of attributes is what stops it being met twice by everyone else:
       aria-hidden takes it out of the accessibility tree and tabindex takes
       it out of the tab order, which between them leave exactly one control
       where the markup has two. */
    select.setAttribute("aria-hidden", "true");
    select.tabIndex = -1;

    wrap.appendChild(button);
    wrap.appendChild(list);

    /* The label named the select by `for`, and the select is now hidden, so
       the visible control would have had no name at all.

       Two things have to happen and they are not the same thing. `for` moves
       to the button so that clicking the label reaches the control a sighted
       visitor can see — that is the pointer half. The name is the aria half,
       and it needs saying separately because a <button> takes its accessible
       name from its own contents and ignores a <label> pointed at it: the
       list is "Which session" followed by whatever is currently chosen, which
       is what the closed native control announces too. */
    if (name) {
      if (!name.id) name.id = base + "-label";
      button.setAttribute("aria-labelledby", name.id + " " + button.id);
      list.setAttribute("aria-labelledby", name.id);
      name.htmlFor = button.id;

      /* Browsers forward a label click to the control it names, which would
         open the menu on its own — but only where the forwarding covers
         buttons, and it is not worth finding out per browser. Cancelling the
         default and doing it here is the same outcome everywhere, once. */
      name.addEventListener("click", function (ev) {
        ev.preventDefault();
        button.focus();
        if (list.hidden) show(); else hide(false);
      });
    }

    function render() {
      var chosen = select.options[select.selectedIndex];
      var text = chosen ? label(chosen) : "";
      /* A no-break space rather than nothing: an empty button has no line box
         and would sit shorter than every field beside it. */
      button.textContent = text || "\u00a0";

      for (var n = 0; n < items.length; n++) {
        items[n].setAttribute("aria-selected", n === select.selectedIndex ? "true" : "false");
      }

      if (select.hasAttribute("aria-invalid")) {
        button.setAttribute("aria-invalid", select.getAttribute("aria-invalid"));
      } else {
        button.removeAttribute("aria-invalid");
      }
      button.disabled = select.disabled;
    }

    function setActive(index) {
      active = index;
      for (var n = 0; n < items.length; n++) {
        if (n === index) items[n].setAttribute("data-active", "");
        else items[n].removeAttribute("data-active");
      }
      if (index < 0) {
        button.removeAttribute("aria-activedescendant");
        return;
      }
      button.setAttribute("aria-activedescendant", items[index].id);

      /* Scrolled by hand rather than with scrollIntoView, which is entitled
         to scroll every ancestor as well — including the page, which would
         drag the button out from under the menu that is anchored to it. The
         list is the options' offsetParent, so offsetTop is already the
         measurement wanted. */
      var top = items[index].offsetTop;
      var bottom = top + items[index].offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    }

    /* The next option in `delta`'s direction that can actually be chosen.
       Nothing there — the end of the list, or only disabled options past
       here — leaves the highlight where it was. */
    function step(from, delta) {
      for (var n = from + delta; n >= 0 && n < items.length; n += delta) {
        if (!select.options[n].disabled) return n;
      }
      return from;
    }

    function edge(delta) {
      return step(delta > 0 ? -1 : items.length, delta);
    }

    function commit(index) {
      var chosen = select.options[index];
      if (!chosen || chosen.disabled) return;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        /* The whole point of leaving the real control in place: anything
           already listening to it — a section script, an app, Shopify's own
           variant handling — hears exactly what the native menu would have
           told it. Bubbling, because a listener is as likely to be on the
           form as on the field. */
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      render();
    }

    function place() {
      wrap.removeAttribute("data-drop");
      var box = button.getBoundingClientRect();
      var below = window.innerHeight - box.bottom;
      /* Only flips when the list genuinely does not fit below *and* there is
         more room above — otherwise a menu near the bottom of a short window
         would flip into an even smaller gap. */
      if (list.offsetHeight > below && box.top > below) wrap.setAttribute("data-drop", "up");
    }

    function show() {
      if (!list.hidden) return;
      if (openMenu && openMenu !== api) openMenu.hide(false);

      /* Re-read the select first. Two sections set these menus in script
         without firing anything — booking-request preselects a session from
         `?session=`, contact drives its own — and both run before this file
         does. The change listener at the bottom catches everything after
         that; this catches everything before it. */
      render();

      list.hidden = false;
      button.setAttribute("aria-expanded", "true");
      place();
      setActive(select.selectedIndex >= 0 ? select.selectedIndex : edge(1));
      openMenu = api;
    }

    function hide(returnFocus) {
      if (list.hidden) return;
      setActive(-1);
      list.hidden = true;
      button.setAttribute("aria-expanded", "false");
      wrap.removeAttribute("data-drop");
      if (openMenu === api) openMenu = null;
      if (returnFocus) button.focus();
    }

    var api = {
      hide: hide,
      /* An outside click is anything not in this control, and the label
         counts as part of it: the label lives outside the wrapper but
         clicking it is how the menu was opened. */
      owns: function (node) {
        if (!node || !node.closest) return false;
        if (node.closest(".select-menu") === wrap) return true;
        return !!(name && (node === name || name.contains(node)));
      }
    };

    /* ── typeahead ──────────────────────────────
       What the native menu does, and the thing people miss most when a select
       is replaced: type "th" and land on Thursday. One letter repeated cycles
       through the options starting with it, anything longer is a prefix — the
       same two rules every platform menu follows. */
    var typed = "";
    var timer = null;

    function repeated(text) {
      for (var n = 1; n < text.length; n++) {
        if (text.charAt(n) !== text.charAt(0)) return false;
      }
      return true;
    }

    function find(prefix, from) {
      for (var n = 1; n <= items.length; n++) {
        var at = (from + n) % items.length;
        if (select.options[at].disabled) continue;
        if (label(select.options[at]).toLowerCase().indexOf(prefix) === 0) return at;
      }
      return -1;
    }

    function typeahead(character) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { typed = ""; }, TYPEAHEAD_RESET);
      typed += character.toLowerCase();

      var current = list.hidden ? select.selectedIndex : active;
      if (current < 0) current = 0;

      /* A repeated letter searches from *after* the current option, so it
         steps on each time. A growing prefix searches from the current one,
         so "t", "th", "thu" all stay on Thursday rather than walking away. */
      var cycling = repeated(typed);
      var found = find(cycling ? typed.charAt(0) : typed, cycling ? current : current - 1);
      if (found < 0) return;

      if (list.hidden) commit(found);
      else setActive(found);
    }

    button.addEventListener("click", function () {
      if (list.hidden) show(); else hide(true);
    });

    button.addEventListener("keydown", function (ev) {
      var key = ev.key;
      /* Older WebKit reports the space bar and Escape by their long names. */
      var space = key === " " || key === "Spacebar";
      var escape = key === "Escape" || key === "Esc";

      if (list.hidden) {
        if (space || key === "Enter" || key === "ArrowDown" || key === "ArrowUp") {
          ev.preventDefault();
          show();
          return;
        }
        /* Home and End on a closed menu choose outright, the way they do on
           a native one — there is nothing open for them to move around in. */
        if (key === "Home" || key === "End") {
          ev.preventDefault();
          commit(edge(key === "Home" ? 1 : -1));
          return;
        }
      } else {
        if (key === "ArrowDown") { ev.preventDefault(); setActive(step(active, 1)); return; }
        if (key === "ArrowUp") { ev.preventDefault(); setActive(step(active, -1)); return; }
        if (key === "Home") { ev.preventDefault(); setActive(edge(1)); return; }
        if (key === "End") { ev.preventDefault(); setActive(edge(-1)); return; }
        if (space || key === "Enter") { ev.preventDefault(); commit(active); hide(true); return; }
        if (escape) { ev.preventDefault(); hide(true); return; }
        /* Tab takes the highlighted option with it, which is what the native
           menu does. Deliberately not prevented: the point of Tab is that
           focus carries on to the next field. */
        if (key === "Tab") { commit(active); hide(false); return; }
      }

      /* Anything that prints one character is a search. Modified keys are
         shortcuts belonging to the browser, and the space bar was already
         answered above. */
      if (key && key.length === 1 && !space && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
        ev.preventDefault();
        typeahead(key);
      }
    });

    /* Pressing on an option must not take focus off the button — it is the
       button that holds aria-activedescendant and every key handler, and a
       menu that loses its keyboard the moment a mouse touches it is a menu
       nobody can finish with the keyboard. */
    list.addEventListener("mousedown", function (ev) {
      /* Narrowed to the rows on purpose: prevented across the whole list it
         would also cancel a drag on the list's own scrollbar. */
      if (ev.target.closest && ev.target.closest(".select-menu-option")) ev.preventDefault();
    });

    list.addEventListener("click", function (ev) {
      var target = ev.target.closest ? ev.target.closest(".select-menu-option") : null;
      if (!target) return;
      var index = items.indexOf(target);
      /* A disabled option is not a miss to be closed on; it is a row that
         does nothing, exactly as it does in the native menu. */
      if (index < 0 || select.options[index].disabled) return;
      commit(index);
      hide(true);
    });

    /* The other half of the contract. Something else setting the value — the
       two section scripts, an app, a restored form after a failed submit —
       leaves the button saying the old thing unless it is told. */
    select.addEventListener("change", render);

    render();
  }

  function enhanceAll() {
    var selects = document.querySelectorAll(".field select");
    for (var i = 0; i < selects.length; i++) enhance(selects[i]);
  }

  document.addEventListener("click", function (ev) {
    if (openMenu && !openMenu.owns(ev.target)) openMenu.hide(false);
  });

  /* Escape while the menu is open, wherever the key was caught. The button
     handler above has it first in the ordinary case; this is for the case
     where focus has been moved somewhere unexpected. */
  document.addEventListener("keydown", function (ev) {
    if (!openMenu) return;
    if (ev.key === "Escape" || ev.key === "Esc") openMenu.hide(false);
  });

  /* Focus arriving anywhere else — a click straight into the next field, or
     a screen reader moving through the form — closes it. */
  document.addEventListener("focusin", function (ev) {
    if (openMenu && !openMenu.owns(ev.target)) openMenu.hide(false);
  });

  /* The list is positioned against its own button rather than against the
     window, so scrolling carries it along and needs no handling. A resize is
     different: it re-lays out the form underneath an open menu, and the
     up-or-down decision was made against a window that no longer exists. */
  window.addEventListener("resize", function () {
    if (openMenu) openMenu.hide(false);
  });

  /* Shopify swaps section markup in and out while she edits. New markup
     brings unenhanced selects; the guard at the top of `enhance` is what
     stops the ones already on the page being wrapped a second time. */
  document.addEventListener("shopify:section:load", enhanceAll);
  document.addEventListener("shopify:section:unload", function () {
    if (openMenu) openMenu.hide(false);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", enhanceAll);
  } else {
    enhanceAll();
  }
})();
